"use client";

/**
 * The primary entry point for the Royal Blue theme's homepage.
 *
 * Responsibilities:
 *  - Handles global loading and error states for CMS content.
 *  - Orchestrates all section components (Hero, About, Menu, etc.)
 *  - Wraps each section in a `SectionErrorBoundary` to ensure that
 *    a runtime error in one section doesn't crash the entire experience.
 */

import React, { Suspense } from "react";
import { useAppSelector } from "@/store/hooks";
import { ContentLoader } from "@/ui";

// Section Components
import Hero from "../components/Hero/Hero";
import About from "../components/About/About";
import JoinUs from "../components/About/JoinUs";
import Reservation from "../components/Reservation/Reservation";
import Menu from "../components/Menu/Menu";
import Events from "../components/Events/Events";
import Testimonials from "../components/Testimonials/Testimonials";
import Footer from "../components/Footer/Footer";
import Navbar from "../components/Navbar/Navbar";
import BackToTop from "../components/BackToTop/BackToTop";

// Error Safeguards
import { FeatureErrorBoundary } from "@/Shared/ErrorBoundary";

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

export default function HomeClient() {
  const {
    data: content,
    loading,
    error,
  } = useAppSelector((state) => state.content);

  // ── Loading State ──
  if (loading) {
    return <ContentLoader message="Preparing your culinary experience..." />;
  }

  // ── Error State (CMS failed to load completely) ──
  if (error) {
    return (
      <ContentLoader message="We're experiencing technical difficulties. Please refresh the page." />
    );
  }

  // ── No Data State ──
  if (!content) {
    return (
      <ContentLoader message="One moment, we're crafting your experience..." />
    );
  }

  return (
    <main className="font-inter">
      <FeatureErrorBoundary featureName="Navbar">
        <Navbar />
      </FeatureErrorBoundary>

      <FeatureErrorBoundary featureName="Hero">
        <Hero />
      </FeatureErrorBoundary>

      <FeatureErrorBoundary featureName="About">
        <About />
      </FeatureErrorBoundary>

      <FeatureErrorBoundary featureName="Join Us">
        <JoinUs />
      </FeatureErrorBoundary>

      <FeatureErrorBoundary featureName="Menu">
        <Menu />
      </FeatureErrorBoundary>

      <FeatureErrorBoundary featureName="Testimonials">
        <Testimonials />
      </FeatureErrorBoundary>

      <FeatureErrorBoundary featureName="Events">
        <Events />
      </FeatureErrorBoundary>

      <FeatureErrorBoundary featureName="Reservation">
        <Suspense>
          <Reservation />
        </Suspense>
      </FeatureErrorBoundary>

      <FeatureErrorBoundary featureName="Footer">
        <Footer />
      </FeatureErrorBoundary>

      <FeatureErrorBoundary featureName="Back To Top">
        <BackToTop />
      </FeatureErrorBoundary>
    </main>
  );
}
